import type { LiveTopic, SidebarView } from '@forecastify/shared';
import { ApprovalsRoute, RequestsRoute, ToDecideRoute } from './screens/leave/requests-route';
import { RequestRoute } from './screens/leave/request-route';
import {
  LeaveDashboardRoute,
  LoggingDashboardRoute,
  ResourceDashboardRoute,
  UtilizationDashboardRoute,
} from './screens/dashboards/dashboards-route';
import type { ReactElement } from 'react';
import type { IconName } from '@forecastify/ui';
import {
  AGENT_FLAG,
  AUTH_PATHS,
  DEALS_ENABLED,
  PASSWORD_RESET_PATHS,
  PASSWORD_RESET_ROUTE,
  REPORTS_FLAG,
} from '@forecastify/shared';
import type { MessageKey } from './i18n';
import { AccountRoute } from './screens/account/account-route';
import { ClientsRoute, ProjectRoute, ProjectsRoute } from './screens/delivery/delivery-route';
import {
  ContractPageRoute,
  ContractsRoute,
  LineItemPageRoute,
  PeriodPageRoute,
} from './screens/contracts/contracts-route';
import { InvoicePageRoute, InvoicesRoute } from './screens/contracts/invoices-route';
import { CostPageRoute } from './screens/contracts/cost-route';
import { DealPageRoute, DealsRoute } from './screens/deals/deals-route';
import { SettingsDealsRoute } from './screens/deals/deal-settings-route';
import { FileRoute } from './screens/files/file-route';
import { TaskRoute } from './screens/tasks/task-route';
import {
  SettingsAuditRoute,
  SettingsBillingRoute,
  SettingsDataRoute,
  SettingsGeneralRoute,
  SettingsIntegrationInstanceRoute,
  SettingsIntegrationProviderRoute,
  SettingsIntegrationsRoute,
  SettingsMetafieldsRoute,
  SettingsPermissionsRoute,
  SettingsProjectRolesRoute,
  SettingsTeamsRoute,
  SettingsVocabularyRoute,
  SettingsHolidaysRoute,
  SettingsLeaveRoute,
  SettingsOvertimeRoute,
  SettingsSkillsRoute,
  SettingsSchedulesRoute,
  SettingsCurrencyRoute,
  SettingsRatesRoute,
  SettingsWorklogRoute,
} from './screens/settings/settings-route';

import { TasksRoute } from './screens/tasks/tasks-route';
import { LogsRoute, WorkspaceLogsRoute } from './screens/time/logs-route';
import { TimeRoute } from './screens/time/time-route';
import { PeopleRoute, PersonRoute } from './screens/people/people-route';
import { BuilderRoute, ReportsRoute } from './screens/reports/reports-route';
import { AgentRoute } from './screens/agent/agent-route';
import { DashboardRoute } from './screens/dashboard/dashboard-route';
import { InviteScreen } from './screens/invitations/invite-screen';
import { ForgotPasswordScreen } from './screens/auth/forgot-password';
import { ResetPasswordScreen } from './screens/auth/reset-password';
import { SignInScreen } from './screens/sign-in';
import { OauthConsentScreen } from './screens/oauth/consent-screen';
import { SignUpScreen } from './screens/sign-up';
import { VerifyEmailScreen } from './screens/auth/verify-email-screen';
import { OAuthCallbackScreen } from './screens/auth/oauth-callback-screen';
import { SuspendedScreen } from './screens/suspended-screen';
import { SetupScreen } from './screens/setup/setup-screen';
import { Specimen } from './screens/specimen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE ROUTE TABLE IS DATA, so a test can enumerate it.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §4 lists four failures this project actually shipped, and every one of them is about
 * routes:
 *
 *  * *"Seven screens once shipped built, working and reachable only by typing a URL"* — so every
 *    entry declares whether it has an **entrance somebody can click**, and `entrance: 'none'` is a
 *    deliberate statement rather than an omission;
 *  * *"twelve more rendered fine and were checked by nothing because they lacked the marker the
 *    check looked for"* — so `marker` is required, and `__tests__/routes.test.tsx` asserts that the
 *    address renders **its own** screen and not a neighbour's;
 *  * *"a route silently fell through to the catch-all"* — hence the not-found entry being in this
 *    table too, and a case that a real path never reaches it.
 *
 * ⚠️ **Adding a screen without adding a row here is the failure mode.** The reachability test walks
 * this table, so a screen missing from it is a screen checked by nothing — exactly the twelve. It
 * cannot be enforced from inside a test; it is enforced by this being the only place `element` is
 * written, and by the router below consuming nothing else.
 */

export interface RouteEntry {
  /**
   * The address, exactly as a browser would show it. May contain `:params`.
   *
   * ⚠️⚠️ **R-38 · A WORKSPACE-SCOPED ROUTE IS WRITTEN `scoped('/tasks')`, NOT `'/:workspace/tasks'`.**
   * The active workspace is the first segment of every screen's address, and `scoped()` is the one
   * place the segment's name is written — `workspace-path.ts` holds it, `useWorkspaceSlug()` reads
   * it back, and a hand-typed prefix that disagreed would make every screen report "no workspace"
   * with nothing in a path looking wrong.
   *
   * ⚠️ **Nine addresses stay at the ROOT and must**: `/sign-in`, `/sign-up`, `/invite/:token`,
   * `/auth/verify-email`, the password-reset pair, `/welcome`, `/suspended` and `/specimen`. A
   * person on any of those has no workspace to name. `RESERVED_WORKSPACE_SLUGS` is that list from
   * the other side — the slugs a workspace may not take, because these routes would shadow them —
   * and `routes.test.tsx` asserts the two agree, so adding a root route here without adding the
   * word there fails the build.
   */
  readonly path: string;
  /**
   * Renders while SIGNED OUT, by address.
   *
   * ⚠️⚠️ **`/invite/:token` IS THE ONLY ONE BESIDES SIGN-IN, and without this flag the page could
   * never do its job.** `Gate` renders the sign-in screen for every address while signed out — which
   * is right for the app and wrong for an invitation, because the invitation's whole audience is
   * people who have no account. A visitor who lands here must see what they were invited to before
   * being asked to sign in; the alternative is a login form with no explanation of why.
   */
  readonly signedOutOk?: true;
  /**
   * Renders ONLY while signed out, and redirects a signed-in visitor to `HOME_PATH`.
   *
   * ⚠️⚠️ **THIS FIELD REPLACED A GROWING LIST OF HARDCODED PATH COMPARISONS**, which is the drift
   * this table exists to prevent. `Chrome` carried `current?.path === SIGN_IN_PATH` because a
   * signed-in person left on the sign-in form reads it as the login having failed — reported by the
   * product owner in exactly those words. S1 added the same line for `/welcome`, S2 needed a third
   * for `/sign-up`, and `routes.test.tsx` was excluding each of them from its sweep by name. Three
   * places had to agree per route, by hand.
   *
   * ⚠️ **It is NOT `signedOutOk`'s opposite, and the two must never both be set.** `signedOutOk`
   * means *"renders whatever the session state is"* — an invitation link, a confirmation landing.
   * This one means *"renders only when there is no session"*. A route with both would be rendered
   * by the open-route branch to a signed-in visitor and never reach the redirect.
   */
  readonly signedOutOnly?: true;
  /**
   * `Gate` renders this screen **in place of whatever address was asked for**, for a session in one
   * particular state — and a session not in that state must never see it.
   *
   * ⚠️⚠️ **THIS FIELD REPLACED THE SECOND GROWING LIST OF HARDCODED PATH COMPARISONS, and the first
   * one's own note predicted it.** `signedOutOnly` was introduced to kill `current?.path ===
   * SIGN_IN_PATH`; `/welcome` then needed `route.path !== WELCOME_PATH` in the reachability sweep
   * and a `current?.path === WELCOME_PATH` bounce in `Chrome`, and `routes.test.tsx` wrote *"it was
   * a list of `route.path !== …` comparisons and grew by one per chunk"*. S3's `/suspended` is the
   * third, so it is a declaration rather than a fourth comparison.
   *
   * ⚠️ **It is NOT `signedOutOnly` inverted.** These routes are for somebody who IS signed in, with
   * a live session — they are states of the *account* (no membership yet) or of the *tenant*
   * (suspended), which no flag about being signed out can express.
   */
  readonly renderedInPlaceBy?: 'gate';
  /**
   * A concrete address for a parameterised `path`.
   *
   * ⚠️ **Required whenever `path` has a `:param`, because a test cannot render `/projects/:id`.**
   * Without it the reachability case would navigate to the literal string, land on the catch-all,
   * and report *"the route rendered the wrong screen"* — a failure about the test rather than about
   * the app, on the one gate that exists to be trusted. `routes.test.tsx` asserts the field is
   * present for every path that needs it.
   */
  readonly example?: string;
  /**
   * A `data-screen` value unique to this screen.
   *
   * ⚠️ The screen sets it on its own outermost element. A shared marker would make every "the
   * address renders its own screen" assertion pass for the wrong reason.
   */
  readonly marker: string;
  /** For the document title, so a tab says where somebody is. */
  readonly title: MessageKey;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE ON-SCREEN HEADER, WHERE IT DIFFERS FROM THE TAB.             │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * `title` was doing both jobs and they are not the same job. A tab benefits from context — you
   * have nine of them open and *"Jogosultságok · Beállítások"* is how you find the right one. The
   * bar at the top of the screen you are already looking at does not: it read
   * *"Jogosultságok · Beállítások"* one click into a section whose own navigation is on screen,
   * naming where you are twice and where you came from once.
   *
   * ⚠️ **Absent means `title`, which is right for nearly every route.** Only the two settings
   * areas need the split, because only they have a second level of navigation inside one heading.
   */
  readonly heading?: MessageKey;
  /**
   * How a person gets here without typing.
   *
   * `'sidebar'` — a navigation link. `'none'` — deliberately unreachable, with the reason. There
   * is no third value yet; a screen reached from a row menu or a card gets one when it arrives.
   */
  readonly entrance: 'sidebar' | 'none';
  /** Why, when `entrance` is `'none'`. Required, so "unreachable" is always an argued position. */
  readonly unreachableBecause?: string;
  /**
   * Where in the sidebar a `'sidebar'` entrance sits.
   *
   * ⚠️⚠️ **HERE, AND NOT IN `app.tsx`, BECAUSE THAT IS THE POINT OF THIS TABLE.** The chrome used to
   * carry a hand-written list of paths and labels beside this hand-written table, which is the drift
   * `app.tsx`'s own header warns about — and it drifted: the chrome kept a bespoke `<nav>` for five
   * chunks while `AppShell` sat unused in `packages/ui`. The sidebar now reads this field, so a route
   * cannot appear in one and not the other.
   *
   * `'primary'` is the mockup's top group (`Kezdőlap`, `Feladataim`, `Időrögzítés`, `Kérelmeim`,
   * `Agent`); `'dashboards'` is Phase 3's own collapsible `Dashboardok` section above the projects;
   * `'workspace'` is its `Workspace` group at the bottom of the scroll region; `'projects'` and
   * `'reports'` are the pinned Projektek and Riportok sections' own headings, links rather than rows.
   *
   * ⚠️ **The two request screens sit in DIFFERENT groups, and the mockup is explicit about it.**
   * *Kérelmeim* is one of the five things a person does about their own work, so it is primary,
   * between Időrögzítés and Agent. *Kérelmek* is an administrative queue about everybody, so it
   * belongs beside Dolgozók in `Workspace`. Putting both in one group — which is what we shipped —
   * reads as one feature listed twice.
   */
  readonly group?: 'primary' | 'dashboards' | 'requests' | 'projects' | 'reports' | 'workspace';
  /**
   * The feature flag this route's ENTRANCE depends on.
   *
   * ⚠️⚠️ **THE ENTRANCE, NOT THE ROUTE.** The address keeps working with the flag off — it has to,
   * because a route that stops existing falls through to the catch-all, which is one of the four
   * failures CLAUDE.md §4 names. What goes away is the sidebar row: the endpoints refuse with
   * `feature_not_enabled` (rule 6 — the server check is the only security), and a navigation row
   * leading to a 403 is a broken application rather than a plan boundary.
   *
   * ⚠️ **Declared here rather than filtered in `app.tsx`**, so `routes.test.tsx` can assert that a
   * flagged route loses its entrance and an unflagged one never does — and so the answer to *"what
   * disappears on the cheaper plan"* is one grep rather than a read of the chrome.
   */
  readonly feature?: string;
  /**
   * The ENTITLEMENT this route's entrance depends on — the second reason a nav row is absent.
   *
   * ⚠️⚠️ **`feature` IS ABOUT THE TENANT'S PLAN; THIS IS ABOUT THE PERSON.** A row can be paid for
   * and still lead nowhere: *Kérelmek* for somebody who manages nobody is an empty screen, and
   * *Kérelmeim* for a contractor whose leave this workspace does not manage is a form that refuses
   * every submission. Both were reported in one sitting.
   *
   * ⚠️ **It is not a permission key**, deliberately — the client holds the permission set and could
   * evaluate one. Neither of these is a permission: approving is `leave.decide` OR being somebody's
   * manager, and requesting turns on a column. `sidebar.ts`'s note carries the argument; the server
   * answers and this names which answer to read.
   */
  /**
   * The sidebar entitlement that draws this route's row — the server answers each
   * (`sidebar.service.ts`). 22 Oct 2026 · the workspace menu's rows, Riportok and the dashboards
   * joined the three leave ones: a permission each (`catalogue.ts`).
   */
  readonly entitlement?: keyof SidebarView['entitlements'];
  /** The sidebar row's icon. Required for a `'sidebar'` entrance. */
  readonly icon?: IconName;
  /** The sidebar row's label. A catalogue key, like `title`. */
  readonly navLabel?: MessageKey;
  /**
   * Whether the route renders inside the application chrome.
   *
   * ⚠️ **`'bare'` exists because NESTING TWO SHELLS is a real defect, not a styling choice.**
   * The specimen sheet renders its own `AppShell`, which has its own `<nav>` — inside the chrome
   * that has one too, that is two navigations on one page. A screen reader is offered two sidebars
   * and neither is labelled as the odd one out, and the reachability test could not tell which nav
   * it was asked about.
   *
   * The sign-in and password-reset screens will want the same thing for the same reason: a page
   * with a sidebar to a workspace you are not yet in is a page offering links that cannot work.
   */
  readonly chrome: 'shell' | 'bare';
  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠⚠️ WHAT THIS SCREEN READS, so a change to it arrives without a reload.       │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * C21 · `decisions.md` R-19, and CLAUDE.md §3 states it as a rule: **every route that reads data
   * is live, or says why not.** One of `live` and `notLiveBecause` is required, and
   * `__tests__/live.test.tsx` fails the build when neither is present.
   *
   * ⚠️ **The same shape as `unreachableBecause`, and for the same reason.** That field exists
   * because seven screens once shipped reachable only by typing a URL — a table where the omission
   * has to be *written down* is the mechanism that caught it. A screen that quietly stops updating
   * is the same failure one layer along: nothing errors, nothing logs, and the person watching it
   * simply sees numbers that disagree with the ones beside them.
   *
   * ⚠️ Take a named set from `live/topics.ts` rather than writing an array here. The screen passes
   * the same constant to `useLiveInvalidation`, so there is one declaration with two readers rather
   * than two declarations that can disagree.
   */
  readonly live?: readonly LiveTopic[];
  /** Why this screen does not update live. Required when `live` is absent. */
  readonly notLiveBecause?: string;
  readonly element: ReactElement;
}

/* C21's dependency map — one declaration, read here and by the screen. */
import {
  ALERT_SCREEN_TOPICS,
  CONTRACT_TOPICS,
  DASHBOARD_TOPICS,
  DEAL_TOPICS,
  HOME_TOPICS,
  MY_WEEK_TOPICS,
  CALENDAR_TOPICS,
  LEAVE_TOPICS,
  OVERTIME_TOPICS,
  TEAM_TOPICS,
  SKILL_TOPICS,
  SCHEDULE_TOPICS,
  MONEY_TOPICS,
  DELIVERY_TOPICS,
  PEOPLE_TOPICS,
  REPORT_TOPICS,
  RESOURCE_TOPICS,
  TASK_TOPICS,
  TIME_TOPICS,
  INTEGRATION_INSTANCE_TOPICS,
  INTEGRATION_TOPICS,
} from './live/topics';
import { WORKSPACE_PREFIX, scoped } from './workspace-path';

export const ROUTES: readonly RouteEntry[] = [
  {
    path: scoped('/projects'),
    entitlement: 'menuProjects',
    group: 'workspace',
    icon: 'folder',
    navLabel: 'nav.projects',
    marker: 'projects',
    title: 'title.projects',
    entrance: 'sidebar',
    chrome: 'shell',
    live: DELIVERY_TOPICS,
    element: <ProjectsRoute />,
  },
  {
    /**
     * ⚠️⚠️ **PROJEKTJEIM — 13 Oct 2026, the product owner.** Everything under *Workspace* is the
     * administrator's global view, and the pinned Projektek heading used to open `/projects`, which
     * is one of them. This is the reader's own: the projects they may see — the ones they are a
     * member of, or all of them when `project.view` is theirs at workspace scope. The server already
     * trims `/projects` that way (`project.service.ts`, P-1), so the difference is where it is
     * reached from and what it calls itself, not a second query.
     *
     * ⚠️ **`group: 'projects'` and no row of its own** — the section heading IS the link
     * (`app.tsx`), and the section stays even when nothing is pinned, so the entrance always
     * exists for whoever can view projects.
     */
    path: scoped('/my-projects'),
    group: 'projects',
    icon: 'folder',
    navLabel: 'nav.myProjects',
    marker: 'my-projects',
    title: 'title.myProjects',
    entrance: 'sidebar',
    chrome: 'shell',
    live: DELIVERY_TOPICS,
    element: <ProjectsRoute mine />,
  },
  {
    /**
     * ⚠️ **`entrance: 'none'`, and it is NOT unreachable — which is the limit of this field.**
     * A project is opened by clicking its row on `/projects`, so nobody has to type this address;
     * but the two values here are `'sidebar'` and `'none'`, and a per-project sidebar link is not a
     * thing. `routes.tsx`'s own note anticipates this: *"a screen reached from a row menu or a card
     * gets one when it arrives."* This is that screen, and the value is not being added yet because
     * one caller does not justify a vocabulary — the reason is written down instead.
     */
    path: scoped('/projects/:id'),
    example: scoped('/projects/11111111-1111-4111-8111-111111111111'),
    marker: 'project',
    title: 'title.projects',
    entrance: 'none',
    unreachableBecause:
      'Opened by clicking a project on /projects, not from the sidebar — a per-project nav link is not a thing. Reachable, but not by an entrance this field can describe.',
    chrome: 'shell',
    /**
     * C59 · ⚠⚠ **THE ALERT TOPICS JOIN THE PROJECT'S OWN, BECAUSE THE ALERTEK TAB LIVES
     * HERE.** R-19 attaches a live declaration to an ADDRESS, and this address now draws ten tabs,
     * the tenth of which reads almost every table in the workspace. Declaring only
     * `DELIVERY_TOPICS` would leave the gate satisfied and the tab stale — the exact shape of
     * failure the rule exists to catch, one level down from where it looks.
     *
     * ⚠️ **The union deduplicates**, and most of `DELIVERY_TOPICS` is already in the alert set.
     * What it genuinely adds is `lineitem`, `contract`, `invoice`, `extcost`, `ratecard`,
     * `schedule`, `currency`, `holiday` and `leave` — nine, and every one of them can turn a
     * finding on or off without touching the project.
     */
    live: [...new Set([...DELIVERY_TOPICS, ...ALERT_SCREEN_TOPICS])],
    element: <ProjectRoute />,
  },
  {
    /**
     * P3-9 · one leave request, in full — the mockup's `leavereq`.
     *
     * ⚠️ **Declared AFTER `/requests`**, and react-router would match both correctly either way (an
     * exact path beats a parameter). Ordered anyway, because the reader's model of the file should
     * be the router's — the argument `/tasks` makes about the same pair.
     *
     * ⚠️ `entrance: 'none'` for the reason `/projects/:id` and `/tasks/:key` both give: it is
     * opened by clicking a row in one of the two queues, and a per-request sidebar link is not a
     * thing.
     *
     * ⚠️ **Leave only, and an overtime claim has no detail.** A claim is one worklog and one
     * multiplier — everything about it already fits in the queue row, so a second screen would be a
     * page whose whole content is the line you clicked to reach it.
     */
    path: scoped('/requests/:id'),
    example: scoped('/requests/11111111-1111-4111-8111-111111111111'),
    marker: 'request',
    title: 'title.request',
    entrance: 'none',
    unreachableBecause:
      'Opened by clicking a leave request in Kérelmeim or Kérelmek, not from the sidebar. The address carries the request id so it can be pasted into a conversation about it.',
    chrome: 'shell',
    live: LEAVE_TOPICS,
    element: <RequestRoute />,
  },
  {
    /**
     * C49 · Logolás. The grid of people × days.
     *
     * ⚠️ **`DASHBOARD_TOPICS` rather than a new set**: it already carries `worklog`, `task` and
     * `project`, and this screen moves when an hour is logged. C42's calendar and C47's leave move
     * it too, which is why both are added to that constant rather than a fourth one declared here.
     */
    path: scoped('/dashboards/logging'),
    entitlement: 'dashboardLogging',
    group: 'dashboards',
    icon: 'layout-grid',
    navLabel: 'nav.dashLogging',
    marker: 'dash-logging',
    title: 'title.dashLogging',
    entrance: 'sidebar',
    chrome: 'shell',
    live: DASHBOARD_TOPICS,
    element: <LoggingDashboardRoute />,
  },
  {
    /**
     * C53 · Erőforrás. The grid of people × columns, planned.
     *
     * ⚠️ **`RESOURCE_TOPICS`, not `DASHBOARD_TOPICS`** — the two sets overlap but say different
     * things: this screen moves on an ALLOCATION, and does not care about a worklog. Reusing the
     * other constant would subscribe it to every hour anybody logs, to redraw a plan none of them
     * changed.
     */
    path: scoped('/dashboards/resource'),
    entitlement: 'dashboardResource',
    group: 'dashboards',
    icon: 'layout-grid',
    navLabel: 'nav.dashResource',
    marker: 'dash-resource',
    title: 'title.dashResource',
    entrance: 'sidebar',
    chrome: 'shell',
    live: RESOURCE_TOPICS,
    element: <ResourceDashboardRoute />,
  },
  {
    /** C49 · Szabadságok. Who is away, and when. */
    path: scoped('/dashboards/leave'),
    entitlement: 'dashboardLeave',
    group: 'dashboards',
    icon: 'layout-grid',
    navLabel: 'nav.dashLeave',
    marker: 'dash-leave',
    title: 'title.dashLeave',
    entrance: 'sidebar',
    chrome: 'shell',
    live: DASHBOARD_TOPICS,
    element: <LeaveDashboardRoute />,
  },
  {
    /**
     * 17 Oct 2026 · Utilizáció — the fourth dashboard (`decisions.md` UT-1). Billable, internal
     * and soft hours against working time. ⚠️ `RESOURCE_TOPICS`, like Erőforrás: it moves on an
     * allocation, a schedule or leave — and not on a logged hour.
     */
    path: scoped('/dashboards/utilization'),
    entitlement: 'dashboardUtilization',
    group: 'dashboards',
    icon: 'layout-grid',
    navLabel: 'nav.dashUtil',
    marker: 'dash-util',
    title: 'title.dashUtil',
    entrance: 'sidebar',
    chrome: 'shell',
    live: RESOURCE_TOPICS,
    element: <UtilizationDashboardRoute />,
  },
  {
    /**
     * C47 · **Kérelmek — every request in the workspace.** The administrator's list: it answers
     * *what is happening in the workspace*, where the two personal screens answer *what is mine*
     * and *what is waiting for me*.
     *
     * ⚠️⚠️ **AND THAT IS WHY IT SITS IN `workspace` RATHER THAN WITH THEM** — the product owner,
     * 20 Sep. Feedback round 4 item 2 split the personal screens into a dropdown of their own; this
     * one was swept in with them and should not have been. It belongs beside the other things a
     * reader administers, which is where it was before the round.
     */
    path: scoped('/approvals'),
    group: 'workspace',
    icon: 'inbox',
    /* ⚠️ The permission and nothing else. Managing people gets you the queue below, not this. */
    entitlement: 'leaveViewAll',
    navLabel: 'nav.approvals',
    marker: 'approvals',
    title: 'title.approvals',
    entrance: 'sidebar',
    chrome: 'shell',
    live: LEAVE_TOPICS,
    element: <ApprovalsRoute />,
  },
  {
    /**
     * C9. The four views — list, board, timeline, grid — behind one toolbar and one filter set.
     *
     * ⚠️ **Declared BEFORE `/tasks/:key`**, and react-router would in fact match them correctly
     * either way (an exact path beats a parameter). Ordered anyway, because the reader's model of a
     * route table is top-down and *"a route silently fell through"* is one of §4's four failures.
     */
    path: scoped('/tasks'),
    entitlement: 'menuTasks',
    group: 'workspace',
    icon: 'list-checks',
    navLabel: 'nav.tasks',
    marker: 'tasks',
    title: 'title.tasks',
    entrance: 'sidebar',
    chrome: 'shell',
    live: TASK_TOPICS,
    element: <TasksRoute />,
  },
  {
    /**
     * ⚠️ **`/tasks/:key`, and the key is the point** — `HSO-131` is what people paste out of a
     * commit message or an email. A uuid here would be a link nobody can read, type or recognise.
     *
     * ⚠️ `entrance: 'none'` for the same reason as `/projects/:id`: it is opened by clicking a
     * task, and a per-task sidebar link is not a thing. Reachable, but not by an entrance this
     * field's two values can describe — see the note there.
     */
    path: scoped('/tasks/:key'),
    example: scoped('/tasks/HSO-1'),
    marker: 'task',
    title: 'title.tasks',
    entrance: 'none',
    unreachableBecause:
      'Opened by clicking a task on a project’s Feladatok tab, not from the sidebar. The address carries the task key so it can be pasted and shared.',
    chrome: 'shell',
    live: TASK_TOPICS,
    element: <TaskRoute />,
  },
  {
    /**
     * ⚠️⚠️ **AN ADDRESS FOR A FILE — 15 Sep.** `file-route.tsx` carries the whole argument: a link
     * in a task's description needs a URL, every file in this product is behind an authenticated
     * fetch, and the two shortcuts (a token in the URL, a cookie on the API) are a capability grant
     * and an authentication rewrite respectively. So the link points at the APP, which already has
     * a session, and this screen fetches the bytes and hands them over.
     *
     * ⚠️ `entrance: 'none'` — it is reached by clicking a link somebody wrote into a description,
     * which is not a sidebar row and never will be.
     *
     * ⚠️ `notLiveBecause`: it reads one immutable blob once and leaves. There is no state on screen
     * for an invalidation to refresh, and subscribing would re-download the file.
     */
    path: scoped('/files/:id'),
    example: scoped('/files/01a0a24f-a2a6-7e24-bc63-af6be30d1c40'),
    marker: 'file',
    title: 'title.tasks',
    entrance: 'none',
    unreachableBecause:
      'Opened by clicking a file link inside a description. The address carries the file id so it can be pasted and shared inside the workspace.',
    chrome: 'shell',
    notLiveBecause:
      'It fetches one immutable blob and navigates away; there is no state on screen to invalidate.',
    element: <FileRoute />,
  },
  {
    /**
     * Kezdőlap. ⚠️ **The mockup's own first screen**, and the third of the three sidebar rows
     * `chunks.md` recorded as absent — `Feladataim` is the last one left.
     *
     * ⚠️ **`/` redirects here** rather than this living AT `/`: a route that only bounces renders
     * no screen, so the *"every route renders its own screen"* gate would have nothing to assert.
     * C16 records that failure exactly, for `/settings`.
     */
    path: scoped('/home'),
    group: 'primary',
    icon: 'home',
    navLabel: 'nav.home',
    marker: 'home',
    title: 'title.home',
    entrance: 'sidebar',
    chrome: 'shell',
    live: HOME_TOPICS,
    element: <DashboardRoute />,
  },
  {
    /**
     * *Feladataim* — the second of the three sidebar rows `chunks.md` recorded as absent, and the
     * one the mockup names between `Kezdőlap` and `Agent`.
     *
     * ⚠️⚠️ **THE TASKS SCREEN WITH ONE FILTER ALREADY ANSWERED, NOT A SECOND SCREEN.** Same
     * route component, same request, same four views — `mine` pins the assignee to the session's
     * own member and drops that chip from the toolbar. A parallel screen would be a second place
     * for every task column, filter and view to drift.
     *
     * ⚠️ Its own `marker`, so the route suite can tell it apart from `/tasks`: two addresses
     * rendering an identically-marked screen is exactly the failure C16 recorded.
     */
    path: scoped('/my-tasks'),
    group: 'primary',
    icon: 'list-checks',
    navLabel: 'nav.myTasks',
    marker: 'my-tasks',
    title: 'title.myTasks',
    entrance: 'sidebar',
    chrome: 'shell',
    live: TASK_TOPICS,
    element: <TasksRoute mine />,
  },
  /**
   * ⚠️⚠️ **THE ORDER OF THIS TABLE IS THE ORDER OF THE SIDEBAR, AND THAT IS WHY THESE TWO MOVED.**
   * The mockup's primary group reads `Kezdőlap · Feladataim · Időrögzítés · Kérelmeim · Agent`;
   * ours came out `Kérelmeim · Kezdőlap · Feladataim · Agent · Időrögzítés`, because a route's
   * position here is its position there and both were declared beside the screens they were built
   * with. Moving the declaration is the fix — a `navOrder` field would be a second place to keep
   * the same fact.
   */
  {
    /**
     * C11. ⚠️ **`/time`, and the address carries no day.** The mockup keeps the selected day in
     * component state; putting it in the URL would make every click on the strip a history entry,
     * so Back walks a week one day at a time instead of leaving the screen. When somebody needs to
     * share a specific day it becomes a query parameter, which is additive.
     */
    path: scoped('/time'),
    group: 'primary',
    /* 26 Oct 2026 · absent for somebody who does not log time — `logsTime`. */
    entitlement: 'logsTime',
    icon: 'clock',
    navLabel: 'nav.time',
    marker: 'time',
    title: 'title.time',
    entrance: 'sidebar',
    chrome: 'shell',
    /* 16 Oct 2026 · *A hét összesítése* is the caller's allocations now — `MY_WEEK_TOPICS`. */
    live: [...new Set([...TIME_TOPICS, ...MY_WEEK_TOPICS])],
    element: <TimeRoute />,
  },
  {
    /**
     * C47 · Kérelmeim. The person's own requests.
     *
     * ⚠️ **The mockup puts these in a group of their own and we have none**, so both request
     * screens join the workspace section. The pair reads correctly there because the second is a
     * queue about everybody rather than about you.
     */
    path: scoped('/requests'),
    /**
     * ⚠️⚠️ **`requests` IS A DROPDOWN IN THE MAIN LIST, BETWEEN IDŐRÖGZÍTÉS AND AGENT.** Both
     * personal request screens live under one *Kérelmek* row that opens — `app-shell.tsx` draws it,
     * `NavItem.items` argues the control. The declaration order here IS the sequence, which is why
     * these two sit exactly where they do in this file.
     *
     * ⚠️ **The WORKSPACE-wide list is not one of them**, and putting it here was the mistake the
     * product owner caught on 20 Sep: it answers a different question and belongs beside the other
     * things a reader administers.
     */
    group: 'requests',
    icon: 'inbox',
    /* ⚠️ Absent for anybody whose leave this workspace does not manage — see `entitlement`. */
    entitlement: 'leaveRequest',
    navLabel: 'nav.requests',
    marker: 'requests',
    title: 'title.requests',
    entrance: 'sidebar',
    chrome: 'shell',
    live: LEAVE_TOPICS,
    element: <RequestsRoute />,
  },
  {
    /**
     * C47 · **Elbírálandó kérelmek — what THIS person has to review**, and nothing else: their
     * reports' leave, and the overtime claims that name them as approver.
     *
     * ⚠️⚠️ **A THIRD ADDRESS RATHER THAN A FILTER ON `/approvals`.** Feedback round 4 item 2 asked
     * for a lead's queue, and the lead is exactly the person who may hold no leave permission at
     * all — `/approvals` would be absent for them. Somebody who holds both wants to switch between
     * *the workspace* and *me*, which one row cannot do, and each is a place to come back to.
     *
     * ⚠️ **`entitlement: 'leaveDecide'` — a permission is only one of the ways in.** Managing one
     * person, or being named approver on one overtime claim, IS the entitlement; see
     * `sidebar.service.ts`, which computes it.
     *
     * ⚠️ Its own `marker`, as `/agent/:id` argues at length: a shared one makes the reachability
     * gate pass for the wrong reason, and the failure it would hide here is this address quietly
     * resolving to the workspace-wide list — which renders perfectly and is the wrong page.
     */
    path: scoped('/to-decide'),
    group: 'requests',
    icon: 'inbox',
    entitlement: 'leaveDecide',
    navLabel: 'nav.toDecide',
    marker: 'to-decide',
    title: 'title.toDecide',
    entrance: 'sidebar',
    chrome: 'shell',
    live: LEAVE_TOPICS,
    element: <ToDecideRoute />,
  },
  {
    /**
     * C17 drew this as a shell; C27 connected it. `agent-screen.tsx` argues what is still missing —
     * the history menu and file attachment — and `docs/plan/agent.md` is the whole design.
     *
     * ⚠️⚠️ **`feature: AGENT_FLAG`, AND IT DEFAULTS TO OFF — as `contracts.view` once did, for a
     * different reason.** `features/registry.ts` argues both: contracts are what a higher plan buys,
     * whereas every agent message spends money with a third party, so a tenant who has never been
     * asked does not spend. The ENTRANCE goes away with the flag and the address
     * keeps rendering a screen — a route that stops existing falls through to the catch-all, which is
     * one of the four failures §4 names.
     *
     * ⚠️⚠️ **`notLiveBecause`, RE-ARGUED RATHER THAN LEFT.** The old reason — *"the model is not
     * connected; there is no data on this screen yet"* — became false the moment a turn could arrive,
     * and `__tests__/live.test.tsx` only checks that one of the two fields is *present*. The new
     * reason is a real argument about what a transcript is, and it is the one from
     * `docs/plan/agent.md` §4: a reply is a record of what was read at a moment, and refreshing a
     * table underneath a sentence that describes it makes the sentence false. C27.2's data cards
     * therefore carry the time they were read; `debt.md` holds the row for a *"changed since"* marker.
     */
    path: scoped('/agent'),
    group: 'primary',
    icon: 'sparkles',
    navLabel: 'nav.agent',
    marker: 'agent',
    title: 'title.agent',
    entrance: 'sidebar',
    feature: AGENT_FLAG,
    chrome: 'shell',
    notLiveBecause:
      'a reply is a record of what was read at a moment, and the prose beside a figure cannot be re-derived when the figure changes; data blocks carry the time they were read instead',
    element: <AgentRoute />,
  },
  {
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ ONE THREAD, AT ITS OWN ADDRESS. The same screen as `/agent`, and     │
     * │ a DIFFERENT marker on purpose.                                            │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * `/agent` is a new conversation; this is a particular one. Until now the open thread lived in
     * component state, so a reload dropped whatever was on screen back to an empty composer — and the
     * thread was still there, in a menu, reachable in two clicks by somebody who knew to look. That
     * is the shape `docs/plan/decisions.md` R-19 keeps out of this table: a screen you can only get
     * back to by remembering how.
     *
     * ⚠️⚠️ **THE MARKER IS DISTINCT, AND `agent-screen.tsx` RENDERS WHICHEVER ONE IT WAS GIVEN.** The
     * reachability gate asserts *"the address renders **its own** screen"*, and a shared marker makes
     * both cases pass for the wrong reason — `routes.test.tsx` refuses a duplicate for exactly that
     * reason. It also buys something real here: the failure this would otherwise hide is
     * `/agent/<id>` quietly resolving to `/agent`'s empty composer, which renders perfectly and is
     * the wrong page.
     *
     * ⚠️ **Declared AFTER `/agent`**, which react-router would match correctly either way — an exact
     * segment beats a parameter. Ordered anyway, because *"a route silently fell through"* is one of
     * CLAUDE.md §4's four failures and the reader's model of this table is top-down.
     *
     * ⚠️ **`entrance: 'none'`, and the reasoning is `/projects/:id`'s.** A thread is opened from the
     * history menu or by following a link; a per-conversation sidebar row is not a thing.
     *
     * ⚠️ **`feature: AGENT_FLAG` here too.** The flag takes the entrance away and both addresses keep
     * rendering a screen; leaving it off this one would make the deep link the way around the gate.
     */
    path: scoped('/agent/:conversationId'),
    example: scoped('/agent/11111111-1111-4111-8111-111111111111'),
    marker: 'agent-conversation',
    title: 'title.agent',
    entrance: 'none',
    unreachableBecause:
      'Opened from the Agent’s own history menu, or by following a link somebody kept — a per-conversation sidebar row is not a thing. The address carries the thread so a reload keeps it.',
    feature: AGENT_FLAG,
    chrome: 'shell',
    notLiveBecause:
      'a reply is a record of what was read at a moment, and the prose beside a figure cannot be re-derived when the figure changes; data blocks carry the time they were read instead',
    element: <AgentRoute />,
  },
  /* 21 Oct 2026 · deal management is off until finished — `DEALS_ENABLED`. */
  ...(DEALS_ENABLED
    ? ([
        {
          /**
           * Phase 6 · D3 · *Dealek* — the mockup's Workspace row, with the open-deal count. One route,
           * four modes in `?mode=` (`deals-route.tsx`). DM-1: `deal.view`, own = owns or follows.
           */
          path: scoped('/deals'),
          group: 'workspace',
          icon: 'tag',
          navLabel: 'nav.deals',
          marker: 'deals',
          title: 'title.deals',
          entrance: 'sidebar',
          chrome: 'shell',
          live: DEAL_TOPICS,
          element: <DealsRoute />,
        },
        {
          /** Phase 6 · D5 · one deal, keyed on its public identifier (DM-3, R-57). */
          path: scoped('/deals/:ref'),
          example: scoped('/deals/deal_0123abcd'),
          marker: 'deal',
          title: 'title.deal',
          entrance: 'none',
          unreachableBecause:
            'Opened from a row on /deals, a pipeline card or a starred deal in the sidebar — a per-deal nav link is not a thing.',
          chrome: 'shell',
          live: DEAL_TOPICS,
          element: <DealPageRoute />,
        },
      ] satisfies readonly RouteEntry[])
    : []),
  {
    /**
     * C24 · Szerződések. The Phase 2 mockup puts this row in the Workspace section between
     * Dolgozók and Feladatok, wrapped in its own `sc-if` — the flag is in the mockup too.
     *
     * ⚠️⚠️ **`feature` TAKES THE ENTRANCE AWAY AND LEAVES THE ADDRESS WORKING.** A tenant
     * without the plan gets no sidebar row; the route still resolves, because a route that stops
     * existing falls through to the catch-all (CLAUDE.md §4). The endpoints refuse either way —
     * rule 6, the server check is the only security.
     */
    path: scoped('/contracts'),
    entitlement: 'menuContracts',
    group: 'workspace',
    icon: 'file-text',
    navLabel: 'nav.contracts',
    marker: 'contracts',
    title: 'title.contracts',
    entrance: 'sidebar',
    chrome: 'shell',
    live: CONTRACT_TOPICS,
    element: <ContractsRoute />,
  },
  {
    /**
     * C24 · one contract as a PAGE. The drawer grows into this and the address follows, so a
     * contract is a link somebody can send — `task-route.tsx`'s argument for `/tasks/:key`.
     *
     * ⚠️ **Keyed on the CODE, not the id.** `HSO-C01` is what people say to each other; a uuid in
     * a chat message is a uuid nobody can read back. The code is unique per workspace and generated,
     * so it cannot collide and nobody can retype it wrong.
     */
    path: scoped('/contracts/:code'),
    example: scoped('/contracts/HSO-C01'),
    marker: 'contract',
    title: 'title.contracts',
    entrance: 'none',
    unreachableBecause:
      'Opened by growing the contract drawer to full screen, or by pasting the address. A sidebar row would be a second entrance to the list it already links to.',
    chrome: 'shell',
    live: CONTRACT_TOPICS,
    element: <ContractPageRoute />,
  },
  {
    /**
     * P2-9 · the code names the contract, the ordinal names the line item within it. Neither half
     * is unique alone, which is why the address has two.
     */
    path: scoped('/contracts/:code/:number'),
    example: scoped('/contracts/HSO-C01/1'),
    marker: 'lineitem',
    title: 'title.contracts',
    entrance: 'none',
    unreachableBecause:
      'Opened by growing the line-item drawer to full screen, or by pasting the address. It hangs off a contract, which the sidebar already reaches.',
    chrome: 'shell',
    live: CONTRACT_TOPICS,
    element: <LineItemPageRoute />,
  },
  {
    /**
     * ┌─────────────────────────────────────────────────────────────────────┐
     * │ C57 · Számlák. The mockup's own Workspace row, after Feladatok.     │
     * └─────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️ **Gated on the contracts flag, like everything else in this phase's money layer** — an
     * invoice is billed against a line item or a period, so a tenant without contracts has nothing
     * to list. The address keeps working for the reason the contracts row gives.
     */
    path: scoped('/invoices'),
    entitlement: 'menuInvoices',
    group: 'workspace',
    icon: 'file-text',
    navLabel: 'nav.invoices',
    marker: 'invoices',
    title: 'title.invoices',
    entrance: 'sidebar',
    chrome: 'shell',
    live: CONTRACT_TOPICS,
    element: <InvoicesRoute />,
  },
  {
    /**
     * C57 · one invoice, at an address somebody can send.
     *
     * ⚠️ **Keyed on OUR number (`SZ-5011`), not theirs.** The external number is what the screen
     * leads with, and it is deliberately not unique — `0134` records why — so it cannot address a
     * row. Ours is generated and unique per workspace.
     */
    path: scoped('/invoices/:number'),
    example: scoped('/invoices/SZ-5011'),
    marker: 'invoice',
    title: 'title.invoices',
    entrance: 'none',
    unreachableBecause:
      'A row in the invoice list, or the address. It is reached through the list the sidebar already links to, and through the deliverable it bills.',
    chrome: 'shell',
    live: CONTRACT_TOPICS,
    element: <InvoicePageRoute />,
  },
  {
    /**
     * C62 · one external cost, full screen.
     *
     * ⚠️⚠️ **THERE IS NO WORKSPACE-LEVEL COST LIST TO REACH IT FROM, AND THAT IS THE MOCKUP'S OWN
     * SHAPE.** A cost belongs to a project; the tab is its list. This is the single record at an
     * address, for the same reasons the invoice has one — it is what a finance reader sends
     * somebody when a figure is queried.
     */
    path: scoped('/costs/:number'),
    example: scoped('/costs/EK-3041'),
    marker: 'cost',
    title: 'title.costs',
    entrance: 'none',
    unreachableBecause:
      "A row on the project's Külső költségek tab, the line item's cost section, or the address. There is no workspace-level list of costs in the product, by design.",
    chrome: 'shell',
    live: CONTRACT_TOPICS,
    element: <CostPageRoute />,
  },
  {
    /**
     * P2-14 · a period is derived, so it has no id — but it has a first day, and that is a name
     * the recurrence cannot quietly reassign the way it can an index.
     */
    path: scoped('/contracts/:code/periods/:from'),
    example: scoped('/contracts/HSO-C01/periods/2026-01-01'),
    marker: 'period',
    title: 'title.contracts',
    entrance: 'none',
    unreachableBecause:
      'A row in the contract detail’s Időszakok table, or the address. A period belongs to one contract and is reached through it.',
    chrome: 'shell',
    live: CONTRACT_TOPICS,
    element: <PeriodPageRoute />,
  },
  {
    /**
     * C12. The four log surfaces, under the Time section.
     *
     * ⚠️ **`entrance: 'sidebar'`, and NOT `'none'` with a switcher on `/time`.** The mockup reaches
     * this through a dropdown on the Időrögzítés header (`timeSectionMenu`), which is a real
     * entrance — but this table's two values cannot describe it, and a section switcher is one
     * click deeper than a nav link for a surface somebody uses daily. A second sidebar row is the
     * honest version of "reachable", and `routes.test.tsx` can then click it.
     *
     * ⚠️ The view (list / timesheet / calendar / grid) stays in component state, as C9's four task
     * views do. Putting it in the address would make every view switch a history entry, so Back
     * would walk the four instead of leaving the screen.
     */
    path: scoped('/time/logs'),
    /* 26 Oct 2026 · my own hours — closed, like *Idő*, to somebody who does not log time. */
    entitlement: 'logsTime',
    marker: 'time-logs',
    title: 'title.logs',
    /**
     * ┌───────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE MOCKUP DRAWS BOTH ROWS, AND IT WAS RIGHT — BUT NOT ABOUT THE SCOPE. │
     * └───────────────────────────────────────────────────────────────┘
     *
     * `main.jsx` 2862–2863 has a Workspace row going to the SAME destination the header dropdown
     * selects, so this entry carried both entrances and one scope. The product owner's reading of
     * 20 Sep is that they are two questions: *Időrögzítés › Időlogok* is my hours, the Workspace
     * row is everybody's. Two destinations now — this one and `/worklogs` below.
     *
     * ⚠️⚠️ **`entrance: 'none'`, AND IT IS NOT UNREACHABLE** — `/projects/:id` records the
     * same limit of this field. The time section's header dropdown is a real entrance and a
     * clickable one; it is simply not a sidebar row, and those are the only two values there are.
     * Its Workspace row moved to the address that now answers the Workspace question.
     */
    entrance: 'none',
    unreachableBecause:
      'Reached from the Időrögzítés header dropdown (time-section-header.tsx), beside Áttekintés — a real entrance, but not a sidebar row, which is all this field can describe. The workspace-wide list at /worklogs keeps the sidebar row.',
    chrome: 'shell',
    live: TIME_TOPICS,
    element: <LogsRoute />,
  },
  {
    /**
     * ⚠️⚠️ **WORKSPACE › IDŐLOGOK — everybody's hours.** The row that was on `/time/logs`,
     * pointed at the question it was always labelled with. It sits beside the other things a
     * reader administers, exactly as the workspace-wide *Kérelmek* list sits apart from *Saját
     * kérelmeim* — the same split, made on the same day, for the same reason.
     *
     * ⚠️ **No entitlement or permission gate here.** `worklog.service.ts` narrows every read to
     * the caller's `worklog.read` scope, so somebody holding `own` opens this and sees their own
     * hours. Hiding the row instead would be a second, weaker copy of that rule — and UI gating is
     * UX, never the boundary.
     */
    path: scoped('/worklogs'),
    entitlement: 'menuWorklogs',
    group: 'workspace',
    icon: 'clock',
    navLabel: 'nav.logs',
    marker: 'workspace-logs',
    title: 'title.logs',
    entrance: 'sidebar',
    chrome: 'shell',
    live: TIME_TOPICS,
    element: <WorkspaceLogsRoute />,
  },
  {
    /** C14. */
    path: scoped('/people'),
    entitlement: 'menuPeople',
    group: 'workspace',
    icon: 'users',
    navLabel: 'nav.people',
    marker: 'people',
    title: 'title.people',
    entrance: 'sidebar',
    chrome: 'shell',
    live: PEOPLE_TOPICS,
    element: <PeopleRoute />,
  },
  {
    /**
     * ⚠️ `entrance: 'none'` for the same reason as `/projects/:id` and `/tasks/:key` — opened by
     * clicking a row, and a per-person sidebar link is not a thing.
     */
    path: scoped('/people/:id'),
    example: scoped('/people/11111111-1111-4111-8111-111111111111'),
    marker: 'person',
    title: 'title.person',
    entrance: 'none',
    unreachableBecause:
      'Opened by clicking somebody on /people, not from the sidebar — a per-person nav link is not a thing.',
    chrome: 'shell',
    live: PEOPLE_TOPICS,
    element: <PersonRoute />,
  },
  {
    /** C15. */
    path: scoped('/reports'),
    entitlement: 'reports',
    group: 'workspace',
    /* S-11: the first feature flag, and reporting is why — its own nav item, its own route tree,
     * its own endpoints, so turning it off removes a capability rather than degrading one. */
    feature: REPORTS_FLAG,
    icon: 'chart-column',
    navLabel: 'nav.reports',
    marker: 'reports',
    title: 'title.reports',
    entrance: 'sidebar',
    chrome: 'shell',
    live: REPORT_TOPICS,
    element: <ReportsRoute />,
  },
  {
    /**
     * ⚠️⚠️ **RIPORTJAIM — 13 Oct 2026, the product owner**, the same move *Projektjeim* made: the
     * pinned Riportok heading opened Workspace › Riportok, and the section vanished with no pins, so
     * a reader's reports had no entrance of their own. The list is the same one — P-3 makes every
     * report its author's, so `/reports` already answers with the reader's own — reached from their
     * section and calling itself theirs.
     *
     * ⚠️ **`feature: REPORTS_FLAG`**, like the workspace row: no reporting on the plan, no entrance.
     */
    path: scoped('/my-reports'),
    group: 'reports',
    feature: REPORTS_FLAG,
    icon: 'chart-column',
    navLabel: 'nav.myReports',
    marker: 'my-reports',
    title: 'title.myReports',
    entrance: 'sidebar',
    chrome: 'shell',
    live: REPORT_TOPICS,
    element: <ReportsRoute mine />,
  },
  {
    /**
     * ⚠️ **A STATIC path declared beside `/reports/:id`, and declaration order is not why it works.**
     * React Router ranks static segments above dynamic ones regardless of order, so `/reports/new`
     * cannot fall into the builder as `id = 'new'`. Declared adjacently anyway, so the pair is read
     * together.
     *
     * ⚠️ **A DISTINCT marker, because the two addresses are two states of one screen** — an unsaved
     * builder has no Refresh, no kebab and no `ran_at`, so `report-new` and `report-builder` are
     * genuinely different things to land on, and the "every route has a distinct marker" case is
     * right to insist. The screen derives it from whether it has an id.
     *
     * ⚠️ `entrance: 'none'`, the fourth caller of the limit `/projects/:id` records: reached by the
     * *"Új riport"* button on `/reports`, which is a click somebody can make but not one this
     * field's two values can name.
     */
    path: scoped('/reports/new'),
    marker: 'report-new',
    title: 'title.reportNew',
    entrance: 'none',
    unreachableBecause:
      'Reached by the "Új riport" button on /reports. Reachable by a click, but not by an entrance this field can describe — see /projects/:id.',
    chrome: 'shell',
    notLiveBecause: 'a builder over a definition being typed, not a read of shared data',
    element: <BuilderRoute />,
  },
  {
    path: scoped('/reports/:id'),
    example: scoped('/reports/11111111-1111-4111-8111-111111111111'),
    marker: 'report-builder',
    title: 'title.report',
    entrance: 'none',
    unreachableBecause:
      'Opened by clicking a saved report on /reports — a per-report sidebar link is not a thing.',
    chrome: 'shell',
    live: REPORT_TOPICS,
    element: <BuilderRoute />,
  },
  {
    path: scoped('/clients'),
    entitlement: 'menuClients',
    group: 'workspace',
    icon: 'building',
    navLabel: 'nav.clients',
    marker: 'clients',
    title: 'title.clients',
    entrance: 'sidebar',
    chrome: 'shell',
    live: DELIVERY_TOPICS,
    element: <ClientsRoute />,
  },
  {
    /**
     * ⚠️ **`/account`, not `/settings/account`.** The mockup keeps two separate views — `usettings`
     * for the person and `settings` for the workspace — and nesting the personal one under
     * `/settings` would imply it is a workspace concern. It follows you between workspaces; the
     * address should say so.
     */
    path: scoped('/account'),
    marker: 'account',
    title: 'title.account',
    /* ⚠️ Six sections behind one address, so the bar names the AREA rather than the section. */
    heading: 'title.profileSettings',
    /**
     * ⚠⚠️ **IT LEFT THE SIDEBAR: IT ALREADY HAS AN ENTRANCE, AND TWO IS ONE MORE THAN THE
     * MOCKUP DRAWS.** `markup.html`'s Workspace group is Projektek, Ügyfelek, Dolgozók, Feladatok
     * and Időlogok — five rows about the workspace's WORK. `Fiókom` is reached from the user block
     * at the foot of the sidebar, which is where the mockup puts it and where people look for it.
     *
     * ⚠️ **This is the exact shape C16 recorded when it folded metafields and permissions into the
     * settings rail**: *"keeping their sidebar rows would have been two entrances to one screen"*.
     * Same reasoning, one level up.
     */
    entrance: 'none',
    unreachableBecause:
      'Reached from the user menu at the foot of the sidebar, which is where the mockup puts it. A Workspace-group row as well would be a second entrance to one screen.',
    chrome: 'shell',
    notLiveBecause: 'one person’s own settings — nobody else can change them',
    element: <AccountRoute />,
  },
  {
    /**
     * C16's settings shell. **Seven sections, seven routes** — R-8 chose real paths, so
     * `/settings/worklog` is an address somebody can bookmark rather than a component-state section,
     * and the reachability suite audits every one of them.
     *
     * ⚠️⚠️ **`/settings` IS the Általános section rather than a redirect**, because a route that
     * only bounces renders no screen of its own — and the reachability suite's *"every route renders
     * its own screen"* case then has nothing to assert. It failed exactly that way when this was a
     * redirect. A route the gate cannot check is worse than one address doing double duty.
     *
     * ⚠️⚠️ **THE TWO SECTIONS THAT WERE SIDEBAR LINKS ARE NOW RAIL ENTRIES.** C10's metafields and
     * C5's permissions each had their own `entrance: 'sidebar'` row, because no shell existed when
     * they shipped. Both are sections of the mockup's settings `NAV`, so keeping the sidebar links
     * would be two entrances to one screen — and the rail and the sidebar would be two hand-written
     * lists of the same thing, which is the drift `app.tsx`'s header warns about.
     */
    path: scoped('/settings'),
    /**
     * ⚠️ **Kept for the workspace menu, which is its entrance.** `navLabel` and `icon` are still
     * read there — `app.tsx` builds that menu from `ROUTES`, not from the sidebar list, precisely so
     * this row leaving the nav does not empty it.
     */
    icon: 'settings',
    navLabel: 'nav.settings',
    marker: 'settings-general',
    title: 'title.settings',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    /**
     * ⚠⚠️ **OUT OF THE SIDEBAR FOR `/account`'S REASON.** The mockup reaches workspace settings
     * from the workspace switcher at the top of the sidebar, not from a row in the Workspace group —
     * that group is five rows about the work, and `wsCan` treats settings as a role-gated section
     * behind the switcher. A row here as well would be the second entrance C16 refused for
     * metafields and permissions.
     */
    entrance: 'none',
    unreachableBecause:
      'Reached from the workspace switcher menu at the top of the sidebar, which is where the mockup puts it. The Workspace group is five rows about the work itself.',
    chrome: 'shell',
    notLiveBecause: 'workspace settings, edited by one administrator at a time',
    element: <SettingsGeneralRoute />,
  },
  {
    /** ⚠️ C5's screen, now inside the shell. Its marker stays `permissions`. */
    path: scoped('/settings/permissions'),
    marker: 'permissions',
    title: 'title.permissions',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    notLiveBecause: 'roles, edited by one administrator at a time',
    element: <SettingsPermissionsRoute />,
  },
  {
    /**
     * ⚠️⚠️ **ONE LEVEL'S PERMISSIONS, AT ITS OWN ADDRESS — item 62.** The screen was a list beside
     * a permanently open editor; the feedback asked for two steps, and a step in this product is
     * an address (R-8) rather than a piece of component state. A level somebody is arguing about
     * over chat is now a link, and the browser's Back is the same control as the header's.
     *
     * ⚠️ **Its own `marker`, because the gate requires one per address** — and rightly: two
     * addresses sharing a marker is how a route silently resolves to its neighbour with the check
     * still green. One component renders both steps and names itself after the step it is on.
     */
    path: scoped('/settings/permissions/:roleId'),
    /* ⚠️ The first SEEDED role id would be a fixture this file does not have, so the render check
       gets a real one from the list it renders — an unknown id draws the list, which is the other
       marker. `example` therefore names a role the stub answers with. */
    example: scoped('/settings/permissions/pl3'),
    marker: 'permission-level',
    title: 'title.permissions',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from a row on the permissions list.',
    chrome: 'shell',
    notLiveBecause: 'roles, edited by one administrator at a time',
    element: <SettingsPermissionsRoute />,
  },
  {
    path: scoped('/settings/tasks'),
    marker: 'settings-tasks',
    title: 'title.settingsTasks',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    notLiveBecause: 'the status and priority catalogues, edited by one administrator at a time',
    element: <SettingsVocabularyRoute />,
  },
  ...(DEALS_ENABLED
    ? ([
        {
          /** Phase 6 · D1 · *Deal menedzsment* — statuses, probabilities, reasons, forecast and profiles. */
          path: scoped('/settings/deals'),
          marker: 'settings-deals',
          title: 'title.settingsDeals',
          heading: 'title.settings',
          entrance: 'none',
          unreachableBecause: 'Reached from the settings rail.',
          chrome: 'shell',
          live: DEAL_TOPICS,
          element: <SettingsDealsRoute />,
        },
      ] satisfies readonly RouteEntry[])
    : []),
  {
    /** C31 · the project-role vocabulary. Not `/settings/permissions` — see `0058`. */
    path: scoped('/settings/project-roles'),
    marker: 'settings-project-roles',
    title: 'title.settingsProjectRoles',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    notLiveBecause:
      'a workspace vocabulary of seven rows, edited by one administrator at a time — the same argument the status and priority catalogues make',
    element: <SettingsProjectRolesRoute />,
  },
  {
    /** R-60 · the team vocabulary. */
    path: scoped('/settings/teams'),
    marker: 'settings-teams',
    title: 'title.settingsTeams',
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: TEAM_TOPICS,
    element: <SettingsTeamsRoute />,
  },
  {
    /** ⚠️ C10's screen, now inside the shell. Its marker stays `metafields`. */
    path: scoped('/settings/metafields'),
    marker: 'metafields',
    title: 'title.metafields',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    notLiveBecause: 'the metafield catalogue, edited by one administrator at a time',
    element: <SettingsMetafieldsRoute />,
  },
  {
    /**
     * ⚠️⚠️ **ONE METAFIELD TYPE'S FIELDS, AT ITS OWN ADDRESS — round 5 item 3.** The screen
     * was seven tabs across the top, *"a bit too wide with how many tabs we have"*; it is a table
     * of types that opens one, the shape *Jogosultságok* uses. A step in this product is an
     * address (R-8) rather than component state, which the product owner chose by name.
     *
     * ⚠️ **Its own `marker`, because the gate requires one per address.** One component renders
     * both steps and names itself after the step it is on — `/settings/permissions/:roleId` is the
     * same arrangement, made for the same reason.
     */
    path: scoped('/settings/metafields/:entity'),
    /* ⚠️ A real entity, because an unknown one renders the LIST — which is the other marker, and
       would fail this route's own check. `task` is in `METAFIELD_ENTITIES` and always will be. */
    example: scoped('/settings/metafields/task'),
    marker: 'metafield-entity',
    title: 'title.metafields',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from a row on the metafield type list.',
    chrome: 'shell',
    notLiveBecause: 'the metafield catalogue, edited by one administrator at a time',
    element: <SettingsMetafieldsRoute />,
  },
  {
    /**
     * C54 · the skill vocabulary. **Live**, for the same reason C42's calendar is: four surfaces
     * read this list, and a level reordered in one tab re-sorts every person's skills in another.
     */
    path: scoped('/settings/skills'),
    marker: 'settings-skills',
    title: 'title.settingsSkills',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: SKILL_TOPICS,
    element: <SettingsSkillsRoute />,
  },
  {
    /** C48 · overtime types. Live for C42's reason: the marking control reads this list. */
    path: scoped('/settings/overtime'),
    marker: 'settings-overtime',
    title: 'title.settingsOvertime',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: OVERTIME_TOPICS,
    element: <SettingsOvertimeRoute />,
  },
  {
    /** C46 · leave types and quotas. Live for C42's reason: C47's requests read this vocabulary. */
    path: scoped('/settings/leave'),
    marker: 'settings-leave',
    title: 'title.settingsLeave',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: LEAVE_TOPICS,
    element: <SettingsLeaveRoute />,
  },
  {
    /** C43 · work schedules. Live for C42's reason: the day kernel reads this too. */
    path: scoped('/settings/schedules'),
    marker: 'settings-schedules',
    title: 'title.settingsSchedules',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: SCHEDULE_TOPICS,
    element: <SettingsSchedulesRoute />,
  },
  {
    /**
     * C51 · rate cards. ⚠️ **Live on both money topics**, not just its own: the screen prices a
     * period in a currency, so a currency renamed or retired next door has to reach it. R-19.
     */
    path: scoped('/settings/rates'),
    marker: 'settings-rates',
    title: 'title.settingsRates',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: MONEY_TOPICS,
    element: <SettingsRatesRoute />,
  },
  {
    /**
     * C51 · the currencies a workspace works in, and their rate on every banking day (FX-1 … FX-8).
     * ⚠️ Live on `currency` for both tabs: `currency.rate_set`, a sync and the daily chore all land
     * there, so a rate typed next door reaches the daily table.
     */
    path: scoped('/settings/currency'),
    marker: 'settings-currency',
    title: 'title.settingsCurrency',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: MONEY_TOPICS,
    element: <SettingsCurrencyRoute />,
  },
  {
    /**
     * C42 · the workspace calendar.
     *
     * ⚠️ **Live, unlike every other settings route.** The rest carry `notLiveBecause: 'edited by
     * one administrator at a time'`, which is true of them; this one is read by the day kernel and
     * by two dashboards later in the phase, so a day added here has to reach a screen somebody else
     * is looking at. R-19.
     */
    path: scoped('/settings/holidays'),
    marker: 'settings-holidays',
    title: 'title.settingsHolidays',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    live: CALENDAR_TOPICS,
    element: <SettingsHolidaysRoute />,
  },
  {
    path: scoped('/settings/worklog'),
    marker: 'settings-worklog',
    title: 'title.settingsWorklog',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    notLiveBecause: 'the logging rules, edited by one administrator at a time',
    element: <SettingsWorklogRoute />,
  },
  {
    path: scoped('/settings/integrations'),
    marker: 'settings-integrations',
    title: 'title.settingsIntegrations',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    /* C32 · it lists what is INSTALLED; C33 · an instance is somebody else's act too. */
    live: INTEGRATION_TOPICS,
    element: <SettingsIntegrationsRoute />,
  },
  {
    /**
     * C33 · one system's instances. ⚠️ **Its own address, where the mockup keeps `igView`** — a
     * failing instance is then a link somebody can paste into a chat.
     */
    path: scoped('/settings/integrations/:extensionId'),
    example: scoped('/settings/integrations/jira'),
    marker: 'integration-provider',
    title: 'title.integrationProvider',
    heading: 'title.settings',
    entrance: 'none',
    /* ⚠️ ONE entrance again since item 68: the *Kezelés* link on the integrations catalogue
       card. `entrance: 'none'` means no NAV item rather than *nothing links here* - the marketplace
       that used to be the second entrance is gone, and its install is a switch on that same card. */
    unreachableBecause:
      'Reached from a card on the integrations catalogue — the Kezelés link, beside the switch that turns the extension on.',
    chrome: 'shell',
    live: INTEGRATION_TOPICS,
    element: <SettingsIntegrationProviderRoute />,
  },
  {
    /** C33 · one instance: overview, its run log, and its settings. */
    path: scoped('/settings/integrations/:extensionId/:instanceId'),
    /* ⚠️ A syntactically valid uuid that exists in no workspace: the render check only needs the
       screen to draw itself, and the not-found body is that screen. */
    example: scoped('/settings/integrations/jira/11111111-1111-4111-8111-111111111111'),
    marker: 'integration-instance',
    title: 'title.integrationInstance',
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from a row on a provider’s instance list.',
    chrome: 'shell',
    live: INTEGRATION_INSTANCE_TOPICS,
    element: <SettingsIntegrationInstanceRoute />,
  },
  {
    /**
     * ⚠️ **`/settings/billing`, where the mockup keeps billing outside the settings rail.**
     * `settings-shell.tsx` records why; the short version is that its own `wsCan` treats `billing`
     * as a role-gated section and only its user-menu placement puts it elsewhere.
     */
    path: scoped('/settings/billing'),
    marker: 'billing',
    title: 'title.billing',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail, by whoever holds billing.manage.',
    chrome: 'shell',
    notLiveBecause: 'billing details, edited by one administrator at a time',
    element: <SettingsBillingRoute />,
  },
  {
    path: scoped('/settings/data'),
    marker: 'settings-data',
    title: 'title.settingsData',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail.',
    chrome: 'shell',
    notLiveBecause: 'import and export, whose progress the wizard already polls',
    element: <SettingsDataRoute />,
  },
  {
    /**
     * ux-pass-2 · the workspace audit log. ⚠️ **Not live, by decision (U-8)**: it changes on every
     * write anybody makes, so a subscription would refetch it continuously under somebody reading
     * page three. The page's *Frissítés* re-reads on request.
     */
    path: scoped('/settings/audit'),
    marker: 'settings-audit',
    title: 'title.settingsAudit',
    /* ⚠️ The bar says Beállítások at every level — see `heading`. */
    heading: 'title.settings',
    entrance: 'none',
    unreachableBecause: 'Reached from the settings rail, by whoever holds audit.view.',
    chrome: 'shell',
    notLiveBecause: 'the audit log changes on every write, so it refreshes on request (U-8)',
    element: <SettingsAuditRoute />,
  },
  {
    /**
     * C1's specimen sheet: the primitives in every theme.
     *
     * ⚠️ **`entrance: 'none'` on purpose, and this is what that value is for.** It is a development
     * artefact — a page of components with no product meaning — so a sidebar link to it would be a
     * link into the workshop. Declaring it here rather than leaving it out is what keeps it from
     * being one of the "twelve that were checked by nothing".
     */
    path: '/specimen',
    marker: 'specimen',
    title: 'app.name',
    entrance: 'none',
    unreachableBecause:
      'A development specimen sheet, not a product screen. Reached by typing the address, deliberately.',
    /* It brings its own `AppShell`. See the note on `chrome`. */
    chrome: 'bare',
    notLiveBecause: 'a component catalogue with fabricated props and no data at all',
    element: <Specimen />,
  },
  {
    /**
     * ⚠️⚠️ **THE ONE OTHER ROUTE THAT WORKS WHILE SIGNED OUT.** An invitation arrives in an email
     * belonging to somebody who may have no account at all — so this page has to render, say what the
     * invitation is, and send them to sign in. `chrome: 'bare'` because `AppShell` needs a workspace
     * and a membership, which is exactly what the visitor does not have.
     */
    path: '/invite/:token',
    /* A concrete address for the render check — the token is opaque, so any string will do. */
    example: '/invite/example-token',
    marker: 'invite',
    signedOutOk: true,
    title: 'app.name',
    entrance: 'none',
    unreachableBecause:
      'Reached from a link in an invitation email, by somebody who may not have an account yet. There is no in-app entrance to an invitation addressed to somebody else.',
    chrome: 'bare',
    notLiveBecause: 'read once, signed out, before there is a workspace to subscribe to',
    element: <InviteScreen />,
  },
  {
    /**
     * ⚠️ **`signedOutOk`, for `/invite/:token`'s reason.** The whole audience of a
     * forgotten-password form is people who cannot sign in — without the flag, `Gate` renders the
     * sign-in screen at this address and the link from it goes nowhere.
     *
     * ⚠️ **The path comes from `PASSWORD_RESET_PATHS`, which the API also reads.** `hard-won.md`
     * WP34-2 is the API composing one spelling of a link while the app served another, with nothing
     * anywhere red. The invitation link still has two hand-written spellings; this one has one.
     */
    path: PASSWORD_RESET_PATHS.request,
    marker: 'forgot-password',
    signedOutOk: true,
    title: 'title.forgot',
    entrance: 'none',
    unreachableBecause:
      'Reached from the sign-in screen’s “Elfelejtetted?” link, by somebody who cannot sign in. There is no chrome to put an entrance in, because the chrome only exists once you are signed in.',
    chrome: 'bare',
    notLiveBecause: 'signed out — there is no session to open a stream with',
    element: <ForgotPasswordScreen />,
  },
  {
    /**
     * ⚠️ **The token is a path parameter, and this route is `signedOutOk` for the same reason as
     * the one above.**
     *
     * ⚠️ **It is NOT `AUTH_PATHS.resetPassword`.** That constant is GoTrue's own recovery
     * redirect, allow-listed in `config.toml` for a flow this product deliberately does not use —
     * its link carries a `#access_token` fragment, which is a different contract from a token in a
     * path. One screen cannot serve both, and putting this one at that address would leave it
     * receiving a shape it does not understand. `password-reset.ts` argues the whole choice.
     */
    path: PASSWORD_RESET_ROUTE,
    /* A concrete address for the render check — the token is opaque, so any string will do. */
    example: `${PASSWORD_RESET_PATHS.redeem}/example-token`,
    marker: 'reset-password',
    signedOutOk: true,
    title: 'title.reset',
    entrance: 'none',
    unreachableBecause:
      'Reached from a link in a password-reset email, by somebody who cannot sign in. There is no in-app entrance to somebody else’s reset link.',
    chrome: 'bare',
    notLiveBecause: 'signed out — there is no session to open a stream with',
    element: <ResetPasswordScreen />,
  },
  {
    /**
     * ⚠️⚠️ **THE SECOND ROUTE `Gate` RENDERS IN PLACE OF WHATEVER ADDRESS YOU ASKED FOR.** Signed
     * out, every address renders `/sign-in`; signed in with no membership, every address renders
     * this. Both are states rather than destinations, and both are listed here anyway so the
     * reachability suite covers them — a screen the router does not know about is a screen no gate
     * can check.
     *
     * ⚠️ **`chrome: 'bare'`** for `sign-in.tsx`'s reason: `AppShell` needs a workspace and a
     * membership, which is exactly what somebody on this screen does not have.
     *
     * ⚠️⚠️ `0202` · **THE FIRST-RUN SETUP FLOW, STEPS 2–4**, where the welcome screen was. `Gate`
     * renders it with no workspace (step 2) and with a workspace whose setup is unfinished (step
     * 3) — the second state is new, and it is why the address is kept rather than a new one added.
     */
    path: '/welcome',
    marker: 'setup',
    title: 'setup.title',
    renderedInPlaceBy: 'gate',
    entrance: 'none',
    unreachableBecause:
      'Rendered in place by Gate for a signed-in account that belongs to no workspace, or whose first-run setup is unfinished. There is no link to it, because a workspace you have not created yet cannot be linked to.',
    chrome: 'bare',
    notLiveBecause:
      'the first-run setup flow — before step 2 there is no workspace to subscribe with, and afterwards the screen reads only what its own steps wrote',
    element: <SetupScreen />,
  },
  {
    /**
     * ⚠️⚠️ **THE ENTRANCE THE SELF-SERVE FLOW DID NOT HAVE.** `POST /auth/sign-up` shipped in C4 and
     * nothing in this application ever called it, so S-2's *"sign-up is self-serve"* was true of the
     * API and false of the product: the only way to an account was an invitation from somebody
     * already inside a workspace. S1 built everything behind this form and left it unreachable.
     */
    path: '/sign-up',
    marker: 'sign-up',
    title: 'app.name',
    signedOutOnly: true,
    entrance: 'none',
    unreachableBecause:
      'Reached from the sign-in screen, which is itself reached by being signed out. A sidebar cannot hold a link to it — the chrome only exists after signing in.',
    chrome: 'bare',
    notLiveBecause: 'signed out — there is no session to open a stream with',
    element: <SignUpScreen />,
  },
  {
    /**
     * ⚠️⚠️ **`signedOutOk`, BECAUSE IT HAS TWO SENDERS AND ONE OF THEM IS SIGNED IN.** A signup
     * confirmation arrives to somebody with no session; C6's email-change confirmation arrives to
     * somebody who has one. The page has to render for both, which is what this flag means — and it
     * is why the screen chooses its own action from `session.status` rather than the route choosing
     * for it.
     *
     * ⚠️ **This address has been live and unrouted since C6.** `POST /auth/email` always passed
     * `redirect_to=…/auth/verify-email` correctly, so every email-change confirmation link in this
     * product landed on the catch-all — WP34-2's failure exactly, surviving because the flow ends in
     * a mailbox that nothing in the suite opens.
     */
    path: '/auth/verify-email',
    marker: 'verify-email',
    title: 'app.name',
    signedOutOk: true,
    entrance: 'none',
    unreachableBecause:
      'Reached from a link in a confirmation email — a signup confirmation or an address change. There is no in-app entrance, because arriving here means a mail client sent you.',
    chrome: 'bare',
    notLiveBecause:
      'a one-shot confirmation of a token that arrived by email; nothing on it is shared data somebody else can change',
    element: <VerifyEmailScreen />,
  },
  {
    /**
     * ⚠️⚠️ **DECLARED IN `AUTH_PATHS` SINCE C4 AND ROUTED TO NOTHING UNTIL ITEM 2.** It sat on
     * GoTrue's `additional_redirect_urls` allow-list in `config.toml`, and it was in the shared
     * constants, and no component rendered at it - so a provider round trip would have landed on
     * the catch-all. That is WP34-2's shape exactly, which is the failure `auth-paths.ts` was
     * created to prevent, surviving here because nothing could reach the address until there was a
     * provider to come back from.
     *
     * ⚠️ **`signedOutOnly`**: arriving here means a sign-in is in progress. Somebody who already
     * has a session has no business spending an authorization code.
     */
    path: AUTH_PATHS.oauthCallback,
    marker: 'oauth-callback',
    title: 'app.name',
    signedOutOnly: true,
    entrance: 'none',
    unreachableBecause:
      'Reached only by a redirect from an identity provider. There is no in-app entrance, because arriving here means Google sent you.',
    chrome: 'bare',
    notLiveBecause:
      'a one-shot exchange of an authorization code; nothing on it is shared data somebody else can change',
    element: <OAuthCallbackScreen />,
  },
  {
    /**
     * ⚠️⚠️ **THE THIRD ROUTE `Gate` RENDERS IN PLACE OF WHATEVER ADDRESS YOU ASKED FOR** — and the
     * only one of the three that a *fully valid* session reaches. Signed out, every address renders
     * `/sign-in`; signed in with no membership, `/welcome`; signed in to a **suspended tenant**,
     * this. `decisions.md` S-9.
     *
     * ⚠️ **It is NOT `signedOutOnly` and NOT `signedOutOk`.** The visitor is signed in, their
     * session is live and their membership is active — the tenant is not. Neither flag can describe
     * that, which is why `Gate` has a third branch rather than a fourth flag.
     *
     * ⚠️ **`chrome: 'bare'`**, for `/welcome`'s reason: a sidebar of links that every 403 is worse
     * than no sidebar.
     */
    path: '/suspended',
    marker: 'suspended',
    title: 'app.name',
    renderedInPlaceBy: 'gate',
    entrance: 'none',
    unreachableBecause:
      'Rendered in place by Gate when the tenant behind the session is suspended. There is no link to it, and typing the address while the tenant is healthy is meant to render nothing useful — which is why Chrome bounces it home.',
    chrome: 'bare',
    notLiveBecause:
      'a suspended tenant refuses every workspace read, the stream included — and being reinstated is an operator action, not a row this screen could watch for',
    element: <SuspendedScreen />,
  },
  {
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ C52 · AT THE ROOT, AND FOR A REASON THE OTHER ROOT ROUTES DO NOT  │
     * │ HAVE: THE PERSON IS ABOUT TO CHOOSE WHICH WORKSPACE.                  │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * The eight addresses above are at the root because whoever reaches them has no workspace to
     * name. This one is different in kind: a grant is per workspace (`0124`), and a screen already
     * sitting inside one would have made that choice on somebody's behalf. `oauth` therefore joins
     * `RESERVED_WORKSPACE_SLUGS`, and `0125` repeats it as a CHECK constraint.
     *
     * ⚠️ **`signedOutOk` is wrong here and `Gate` is right.** A signed-out person who follows a
     * client's authorization link must sign in first and come back — which is what the ordinary gate
     * does — because there is nothing on this page a stranger should see.
     */
    path: '/oauth/consent',
    marker: 'oauth-consent',
    title: 'app.name',
    entrance: 'none',
    unreachableBecause:
      'Reached only by `GET /api/oauth/authorize` redirecting a browser here after it has validated an external client. There is no link to it, and typing the address renders the "no longer valid" state — the request handle is the whole capability.',
    chrome: 'bare',
    notLiveBecause:
      'a one-shot authorization handed over in a URL: it is consumed by the decision, and there is no row a stream could tell this screen has changed',
    element: <OauthConsentScreen />,
  },
  {
    path: '/sign-in',
    marker: 'sign-in',
    title: 'app.name',
    signedOutOnly: true,
    /* ⚠️ No sidebar entrance, and the reason is not "we forgot": the chrome only exists once you
     * are signed in, so there is nothing to put a link in. Reached by being signed out. */
    entrance: 'none',
    unreachableBecause:
      'Reached by being signed out, not by a link — the chrome that would hold one only exists after signing in.',
    chrome: 'bare',
    notLiveBecause: 'signed out — there is no session to open a stream with',
    element: <SignInScreen />,
  },
];

/**
 * ⚠️ **Sign-in is a route like any other, and it is the only one reachable while signed out.**
 * Listing it here rather than special-casing it in the router means the reachability test covers it
 * too — a sign-in screen nobody can reach is the worst version of the "reachable only by typing a
 * URL" failure.
 */
export const SIGN_IN_PATH = '/sign-in';

/** Where somebody with no account goes. Reached from the sign-in screen. */
export const SIGN_UP_PATH = '/sign-up';

/**
 * Where a signed-in account with no membership goes.
 *
 * ⚠️ Its own constant for `SIGN_IN_PATH`'s reason: `Gate` renders it, `Chrome` bounces a member off
 * it, and the route test asserts both halves. Three places, one string.
 */
export const WELCOME_PATH = '/welcome';

/**
 * Where a member of a suspended tenant goes.
 *
 * ⚠️ Its own constant for `WELCOME_PATH`'s reason: `Gate` renders it, `Chrome` bounces a healthy
 * session off it, and the route test asserts both halves.
 */
export const SUSPENDED_PATH = '/suspended';

/**
 * Where `/` goes. Its own constant so the redirect and the test agree by construction.
 *
 * ⚠️⚠️ **R-38 · WORKSPACE-RELATIVE, LIKE EVERY OTHER PATH A SCREEN PASSES AROUND.** The registered
 * route is `scoped(HOME_PATH)` — `/:workspace/home` — and a caller navigating here goes through
 * `useWorkspaceNavigate()` or `useWorkspacePath()`, which supply the segment. Baking a concrete
 * workspace into this constant would make it a different address per person, which is not what a
 * module-level constant can be.
 */
export const HOME_PATH = '/home';

/**
 * The route table split by whether an address carries a workspace.
 *
 * ⚠️ **Derived from `ROUTES` rather than listed**, so a new screen joins the right half by having
 * been written with or without `scoped()` — the same property that makes the nav generated rather
 * than typed. `resolveWorkspaceAddress` needs both: the root patterns to recognise an address that
 * wants no workspace, and the scoped ones to tell a pre-R-38 bookmark from a stranger's slug.
 */
export const ROOT_PATHS: readonly string[] = ROUTES.filter(
  (route) => !route.path.startsWith(WORKSPACE_PREFIX),
).map((route) => route.path);

export const SCOPED_PATHS: readonly string[] = ROUTES.filter((route) =>
  route.path.startsWith(WORKSPACE_PREFIX),
).map((route) => route.path);
